import { Controller } from 'stimulus'
import { registerEscLayer, unregisterEscLayer } from '~/common/helpers/esc_stack'

// Full-screen pan/zoom viewer for Mermaid diagrams in chat messages.
//
// Diagrams are injected into messages after render (see
// common/helpers/mermaid.js), so open gestures are caught with delegated
// document listeners on `.js-mermaid-diagram` instead of actions on the
// figures. Clicking a diagram clones its SVG into the stage at natural size
// and opens the dialog fitted to the screen. Pinch / Ctrl·Cmd+wheel zooms
// toward the cursor; unmodified wheel (trackpad two-finger scroll or mouse
// wheel) pans; touch pinch zooms toward the midpoint (and pans with it);
// dragging pans; double-click zooms in. Pinch-out below ~EXIT_SCALE_RATIO of
// the fitted scale closes back to the conversation. A two-finger pinch on an
// inline diagram opens the viewer mid-gesture so zoom continues in full-screen.

const MIN_SCALE = 0.15
const MAX_SCALE = 8
const BUTTON_ZOOM_FACTOR = 1.4
// Close when the user pinches out smaller than this fraction of the fit scale
const EXIT_SCALE_RATIO = 0.35
// How far a message-view pinch must spread before we open the lightbox
const OPEN_PINCH_RATIO = 1.1
// Suppress the synthetic click that follows a touch pinch-open/close
const CLICK_SUPPRESS_MS = 400
// WheelEvent line-mode deltas are "lines", not CSS pixels — scale them so a
// mouse-wheel tick pans a usable distance (DOM_DELTA_LINE = 1).
const WHEEL_LINE_HEIGHT_PX = 16

export default class extends Controller {
  static targets = ['stage', 'content']

  #scale = 1
  #x = 0
  #y = 0
  #naturalWidth = 0
  #naturalHeight = 0
  #fitScale = 1
  #panFrom = null
  #activePointerId = null
  // pointerId → { x, y } for multi-touch pinch inside the open viewer
  #pointers = new Map()
  #pinch = null // { startDistance, startScale, startX, startY, startMidX, startMidY }
  // Two-finger pinch that began on an inline diagram (may open the viewer)
  #messagePinch = null
  // Survives onClose so a compatibility click after pinch-close does not reopen
  #suppressOpenClickUntil = 0

  connect() {
    document.addEventListener('click', this.#onDocumentClick)
    document.addEventListener('keydown', this.#onDocumentKeydown)
    // Bound manually — wheel needs passive:false so preventDefault can stop page scroll
    this.stageTarget.addEventListener('wheel', this.#onWheel, {
      passive: false,
    })
    // Message-view pinch: non-passive touchstart so we can claim a two-finger
    // gesture before the browser commits it to page zoom/scroll.
    document.addEventListener('touchstart', this.#onMessageTouchStart, {
      passive: false,
    })
    document.addEventListener('touchmove', this.#onMessageTouchMove, {
      passive: false,
    })
    document.addEventListener('touchend', this.#onMessageTouchEnd, {
      passive: true,
    })
    document.addEventListener('touchcancel', this.#onMessageTouchEnd, {
      passive: true,
    })
  }

  disconnect() {
    document.removeEventListener('click', this.#onDocumentClick)
    document.removeEventListener('keydown', this.#onDocumentKeydown)
    unregisterEscLayer('lightbox', this.#escLayerClose)
    this.stageTarget.removeEventListener('wheel', this.#onWheel)
    document.removeEventListener('touchstart', this.#onMessageTouchStart)
    document.removeEventListener('touchmove', this.#onMessageTouchMove)
    document.removeEventListener('touchend', this.#onMessageTouchEnd)
    document.removeEventListener('touchcancel', this.#onMessageTouchEnd)
  }

  close() {
    this.element.close()
  }

  onClose() {
    unregisterEscLayer('lightbox', this.#escLayerClose)
    this.contentTarget.replaceChildren()
    this.stageTarget.style.removeProperty('cursor')
    this.#pointers.clear()
    this.#pinch = null
    this.#panFrom = null
    this.#activePointerId = null
    // Clear gesture tracking, but keep #suppressOpenClickUntil so a late
    // compatibility click after pinch-to-close does not reopen the viewer.
    this.#messagePinch = null
  }

  zoomIn() {
    this.#zoomBy(BUTTON_ZOOM_FACTOR, ...this.#stageCenter())
  }

  zoomOut() {
    this.#zoomBy(1 / BUTTON_ZOOM_FACTOR, ...this.#stageCenter())
    this.#closeIfTooSmall()
  }

  reset() {
    this.#fit()
  }

  zoomAtPoint(event) {
    this.#zoomBy(BUTTON_ZOOM_FACTOR, ...this.#stagePoint(event))
  }

  startPan(event) {
    if (event.pointerType === 'touch') {
      // Touch pan/pinch is handled via #pointers so two fingers can pinch
      this.#pointers.set(event.pointerId, {
        x: event.clientX,
        y: event.clientY,
      })
      try {
        this.stageTarget.setPointerCapture(event.pointerId)
      } catch {
        // Capture can fail if the pointer already ended
      }
      if (this.#pointers.size >= 2) {
        this.#beginPinch()
      } else {
        this.#activePointerId = event.pointerId
        this.#panFrom = { x: event.clientX, y: event.clientY }
        this.stageTarget.style.cursor = 'grabbing'
      }
      event.preventDefault()
      return
    }

    if (event.button !== 0) return
    event.preventDefault()
    this.#activePointerId = event.pointerId
    this.stageTarget.setPointerCapture(event.pointerId)
    this.#panFrom = { x: event.clientX, y: event.clientY }
    this.stageTarget.style.cursor = 'grabbing'
  }

  pan(event) {
    if (event.pointerType === 'touch') {
      if (!this.#pointers.has(event.pointerId)) return
      this.#pointers.set(event.pointerId, {
        x: event.clientX,
        y: event.clientY,
      })

      if (this.#pointers.size >= 2) {
        if (!this.#pinch) this.#beginPinch()
        this.#updatePinch()
      } else if (this.#panFrom && event.pointerId === this.#activePointerId) {
        this.#x += event.clientX - this.#panFrom.x
        this.#y += event.clientY - this.#panFrom.y
        this.#panFrom = { x: event.clientX, y: event.clientY }
        this.#apply()
      }
      return
    }

    if (!this.#panFrom || event.pointerId !== this.#activePointerId) return
    this.#x += event.clientX - this.#panFrom.x
    this.#y += event.clientY - this.#panFrom.y
    this.#panFrom = { x: event.clientX, y: event.clientY }
    this.#apply()
  }

  endPan(event) {
    if (event.pointerType === 'touch') {
      this.#pointers.delete(event.pointerId)
      if (this.#pointers.size < 2) {
        this.#pinch = null
        // After a pinch ends, check whether they zoomed out far enough to leave
        this.#closeIfTooSmall()
      } else {
        // Active pair changed (e.g. 3→2 fingers) — re-baseline on the new pair
        this.#beginPinch()
      }
      if (this.#pointers.size === 1) {
        const [id, point] = this.#pointers.entries().next().value
        this.#activePointerId = id
        this.#panFrom = { x: point.x, y: point.y }
      } else if (this.#pointers.size === 0) {
        this.#panFrom = null
        this.#activePointerId = null
        this.stageTarget.style.removeProperty('cursor')
      }
      return
    }

    if (event.pointerId !== this.#activePointerId) return
    this.#panFrom = null
    this.#activePointerId = null
    this.stageTarget.style.removeProperty('cursor')
  }

  #onDocumentClick = (event) => {
    // Ignore synthetic clicks that follow a pinch-open / pinch-close gesture
    if (Date.now() < this.#suppressOpenClickUntil) return
    if (this.#messagePinch?.opened) return

    this.#openFigure(event.target.closest?.('.js-mermaid-diagram'))
  }

  // The figure is role="button" — Enter / Space open it like a click.
  // showModal() returns focus to the figure when the dialog closes.
  #onDocumentKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    if (!event.target.matches?.('.js-mermaid-diagram')) return
    event.preventDefault()
    this.#openFigure(event.target)
  }

  #openFigure(figure) {
    const svg = figure?.querySelector('svg')
    if (!svg) return
    // Multiple conversations can be on the page; only respond to diagrams in ours
    const conversation = this.element.closest('.js-chat-bot-conversation')
    if (conversation && !conversation.contains(figure)) return

    this.#open(svg)
  }

  #escLayerClose = () => this.close()

  #onWheel = (event) => {
    event.preventDefault()

    // Trackpad pinch is ctrl+wheel (meta on some setups). Same modifiers let a
    // mouse zoom without fighting pan — do not key off deltaMode: pixel-mode
    // wheels come from trackpads and some mice alike.
    if (event.ctrlKey || event.metaKey) {
      const factor = Math.exp(-event.deltaY * 0.01)
      this.#zoomBy(factor, ...this.#stagePoint(event))
      this.#closeIfTooSmall()
      return
    }

    // Unmodified wheel = pan (two-finger trackpad scroll, mouse wheel, etc.).
    // Convert line/page deltas to CSS pixels so mouse ticks actually move.
    const [dx, dy] = this.#wheelDeltaPx(event)
    this.#x -= dx
    this.#y -= dy
    this.#apply()
  }

  // WheelEvent.deltaX/Y units depend on deltaMode (0=px, 1=lines, 2=pages).
  #wheelDeltaPx(event) {
    let { deltaX: dx, deltaY: dy, deltaMode } = event
    if (deltaMode === 1 /* DOM_DELTA_LINE */) {
      dx *= WHEEL_LINE_HEIGHT_PX
      dy *= WHEEL_LINE_HEIGHT_PX
    } else if (deltaMode === 2 /* DOM_DELTA_PAGE */) {
      const rect = this.stageTarget.getBoundingClientRect()
      dx *= rect.width || window.innerWidth
      dy *= rect.height || window.innerHeight
    }
    return [dx, dy]
  }

  // --- Message-view pinch → open lightbox ---------------------------------

  #onMessageTouchStart = (event) => {
    if (this.element.open) return
    if (event.touches.length !== 2) {
      if (event.touches.length < 2) this.#messagePinch = null
      return
    }

    const figure = this.#figureForTouches(event.touches)
    if (!figure) {
      this.#messagePinch = null
      return
    }

    const svg = figure.querySelector('svg')
    if (!svg) {
      // Diagram still rendering — do not leave a half-initialized pinch around
      this.#messagePinch = null
      return
    }

    // Claim the gesture before the browser commits it to page zoom/scroll
    event.preventDefault()

    const [a, b] = [event.touches[0], event.touches[1]]
    this.#messagePinch = {
      figure,
      svg,
      startDistance: this.#distance(a.clientX, a.clientY, b.clientX, b.clientY),
      opened: false,
    }
  }

  #onMessageTouchMove = (event) => {
    const pinch = this.#messagePinch
    // Require a full pinch session (not the short-lived click-suppress stub)
    if (!pinch?.figure || !pinch.svg) return
    if (event.touches.length !== 2) return

    // Once the lightbox is open, stage pointer handlers own new gestures —
    // but the opening finger pair often never re-targets to the stage, so
    // keep driving zoom from these document touches until lift.
    const [a, b] = [event.touches[0], event.touches[1]]
    const distance = this.#distance(a.clientX, a.clientY, b.clientX, b.clientY)
    if (!Number.isFinite(pinch.startDistance) || pinch.startDistance <= 0) return

    const ratio = distance / pinch.startDistance

    if (!pinch.opened) {
      if (ratio < OPEN_PINCH_RATIO) return
      // Spread far enough — enter full-screen and keep zooming with this pinch
      event.preventDefault()
      this.#open(pinch.svg)
      pinch.opened = true
      this.#armClickSuppress()
      pinch.startDistance = distance
      pinch.startScale = this.#scale
      pinch.startX = this.#x
      pinch.startY = this.#y
      const mid = this.#midpoint(a.clientX, a.clientY, b.clientX, b.clientY)
      const rect = this.stageTarget.getBoundingClientRect()
      pinch.startMidX = mid.x - rect.left
      pinch.startMidY = mid.y - rect.top
      return
    }

    // Gesture may have closed the viewer mid-pinch (zoomed out past exit)
    if (!this.element.open) return

    event.preventDefault()
    this.#applyPinchFrom(
      pinch,
      distance,
      this.#midpoint(a.clientX, a.clientY, b.clientX, b.clientY)
    )
    this.#closeIfTooSmall()
  }

  #onMessageTouchEnd = (event) => {
    if (!this.#messagePinch) return
    if (event.touches.length >= 2) return

    if (this.#messagePinch.opened) {
      this.#closeIfTooSmall()
      this.#armClickSuppress()
    }
    this.#messagePinch = null
  }

  #figureForTouches(touches) {
    const conversation = this.element.closest('.js-chat-bot-conversation')
    let figure = null
    for (const touch of touches) {
      const el = document.elementFromPoint(touch.clientX, touch.clientY)
      const f = el?.closest?.('.js-mermaid-diagram')
      if (!f) return null
      if (conversation && !conversation.contains(f)) return null
      if (figure && figure !== f) return null
      figure = f
    }
    return figure
  }

  #armClickSuppress() {
    this.#suppressOpenClickUntil = Date.now() + CLICK_SUPPRESS_MS
  }

  // --- Stage multi-touch pinch --------------------------------------------

  #beginPinch() {
    const points = [...this.#pointers.values()]
    if (points.length < 2) return
    const [a, b] = points
    const mid = this.#midpoint(a.x, a.y, b.x, b.y)
    const rect = this.stageTarget.getBoundingClientRect()
    this.#pinch = {
      startDistance: this.#distance(a.x, a.y, b.x, b.y),
      startScale: this.#scale,
      startX: this.#x,
      startY: this.#y,
      startMidX: mid.x - rect.left,
      startMidY: mid.y - rect.top,
    }
    this.#panFrom = null
    this.#activePointerId = null
    this.stageTarget.style.cursor = 'grabbing'
  }

  #updatePinch() {
    if (!this.#pinch) return
    const points = [...this.#pointers.values()]
    if (points.length < 2) return
    const [a, b] = points
    const distance = this.#distance(a.x, a.y, b.x, b.y)
    if (!Number.isFinite(distance) || !Number.isFinite(this.#pinch.startDistance))
      return
    if (this.#pinch.startDistance <= 0) return
    this.#applyPinchFrom(
      this.#pinch,
      distance,
      this.#midpoint(a.x, a.y, b.x, b.y)
    )
    this.#closeIfTooSmall()
  }

  // Scale tracks distance / startDistance. Keep the content point that was under
  // the original midpoint fixed under the current midpoint (two-finger pan).
  #applyPinchFrom(pinch, distance, midClient) {
    if (!Number.isFinite(pinch.startDistance) || pinch.startDistance <= 0) return
    if (!Number.isFinite(distance)) return
    const rect = this.stageTarget.getBoundingClientRect()
    const midX = midClient.x - rect.left
    const midY = midClient.y - rect.top
    const ratio = distance / pinch.startDistance
    const next = Math.min(
      Math.max(pinch.startScale * ratio, this.#scaleFloor()),
      MAX_SCALE
    )
    if (!Number.isFinite(next)) return
    const k = next / pinch.startScale
    this.#x = midX - (pinch.startMidX - pinch.startX) * k
    this.#y = midY - (pinch.startMidY - pinch.startY) * k
    this.#scale = next
    this.#apply()
  }

  #open(svg) {
    const clone = svg.cloneNode(true)
    // The inline SVG is capped at the message width (max-width: 100%); undo
    // that and pin the clone to its natural size so transforms have a stable
    // base. Internal url(#id) refs still resolve against the original's defs.
    const viewBox = clone.viewBox?.baseVal
    // baseVal is a 0x0 rect (not null) when the SVG has no viewBox, so test for
    // a real size rather than relying on `??`.
    const rect = svg.getBoundingClientRect()
    this.#naturalWidth = viewBox?.width || rect.width || 1
    this.#naturalHeight = viewBox?.height || rect.height || 1
    clone.style.maxWidth = 'none'
    clone.style.width = `${this.#naturalWidth}px`
    clone.style.height = `${this.#naturalHeight}px`

    this.#panFrom = null
    this.#activePointerId = null
    this.#pointers.clear()
    this.#pinch = null

    this.contentTarget.replaceChildren(clone)
    // showModal() alone is not enough to close on Escape: any mounted ModalComponent instance
    // (there is almost always one — e.g. the feedback widget) unconditionally preventDefault()s
    // every Escape keydown at the window level regardless of whether it's open, which silently
    // suppresses the browser's native <dialog> Escape-dismiss app-wide. registerEscLayer's
    // capture-phase handler runs before that and closes this dialog explicitly. ENG-33816.
    this.element.showModal()
    registerEscLayer('lightbox', this.#escLayerClose)
    // Fit synchronously when possible so a mid-gesture open has a real scale
    // before the next touchmove applies the pinch ratio.
    this.#fit()
  }

  // Fit the diagram to the stage (with a little breathing room) and center it
  #fit() {
    const stage = this.stageTarget.getBoundingClientRect()
    // Dialog may not have layout yet on the very first frame — fall back to viewport
    const stageW = stage.width || window.innerWidth
    const stageH = stage.height || window.innerHeight
    const scale =
      Math.min(stageW / this.#naturalWidth, stageH / this.#naturalHeight) * 0.9
    this.#fitScale = Math.min(Math.max(scale, MIN_SCALE), MAX_SCALE)
    this.#scale = this.#fitScale
    this.#x = (stageW - this.#naturalWidth * this.#scale) / 2
    this.#y = (stageH - this.#naturalHeight * this.#scale) / 2
    this.#apply()
  }

  // Zoom by `factor` keeping the stage point (cx, cy) fixed on screen
  #zoomBy(factor, cx, cy) {
    const next = Math.min(
      Math.max(this.#scale * factor, this.#scaleFloor()),
      MAX_SCALE
    )
    const k = next / this.#scale
    this.#x = cx - k * (cx - this.#x)
    this.#y = cy - k * (cy - this.#y)
    this.#scale = next
    this.#apply()
  }

  // Floor low enough that pinch-out can always reach the exit threshold, even
  // when the fitted scale itself is near MIN_SCALE (very large diagrams).
  #scaleFloor() {
    return Math.min(MIN_SCALE, this.#fitScale * EXIT_SCALE_RATIO * 0.9)
  }

  #closeIfTooSmall() {
    if (!this.element.open) return
    if (this.#scale >= this.#fitScale * EXIT_SCALE_RATIO) return
    // Arm before close: onClose clears #messagePinch which used to be the only
    // click guard, and the compatibility click fires after the dialog is gone.
    if (this.#messagePinch?.opened) this.#armClickSuppress()
    this.close()
  }

  #apply() {
    this.contentTarget.style.transform = `translate(${this.#x}px, ${
      this.#y
    }px) scale(${this.#scale})`
  }

  #stagePoint(event) {
    const rect = this.stageTarget.getBoundingClientRect()
    return [event.clientX - rect.left, event.clientY - rect.top]
  }

  #stageCenter() {
    const rect = this.stageTarget.getBoundingClientRect()
    return [rect.width / 2, rect.height / 2]
  }

  #distance(x1, y1, x2, y2) {
    return Math.hypot(x2 - x1, y2 - y1)
  }

  #midpoint(x1, y1, x2, y2) {
    return { x: (x1 + x2) / 2, y: (y1 + y2) / 2 }
  }
}
